import React, { useState, useRef, useEffect } from 'react';
import { FaSearch, FaBell, FaEnvelope, FaCog, FaPalette, FaEye, FaEyeSlash, FaHome, FaTh, FaTasks, FaComments, FaTicketAlt, FaClipboardList, FaChartBar } from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import userPreferenceService from '../../../services/userPreferenceService';
import messageService from '../../../services/messageService';
import NotificationService from '../../../services/NotificationService';
import { API_BASE_URL } from '../../../configs/apiConfig';
import '../styles/UserNavbar.css';
import SettingsService from '../../../services/SettingsService';
import MessageList from '../../../components/message/components/MessageList';
import NotificationList from '../../../components/NotificationList';
import { useNavigate } from 'react-router-dom';

const UserNavbar = ({ 
    darkMode, 
    onThemeChange, 
    opacity, 
    onOpacityChange, 
    blur, 
    onBlurChange,
    baseColor,
    onBaseColorChange,
    backgroundBlur,
    onBackgroundBlurChange,
    userId,
    sectionVisibility,
    onSectionVisibilityChange,
    panels
}) => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [showNotifications, setShowNotifications] = useState(false);
    const [showAppearanceMenu, setShowAppearanceMenu] = useState(false);
    const [showMessages, setShowMessages] = useState(false);
    const [messageType, setMessageType] = useState('inbox');
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationCount, setNotificationCount] = useState(0);
    const [localOpacity, setLocalOpacity] = useState(opacity);
    const [localBlur, setLocalBlur] = useState(blur);
    const [localBackgroundBlur, setLocalBackgroundBlur] = useState(backgroundBlur);
    const [isDarkMode, setIsDarkMode] = useState(darkMode);
    const [localBaseColor, setLocalBaseColor] = useState(baseColor);
    const appearanceMenuRef = useRef(null);
    const messageContainerRef = useRef(null);
    const notificationContainerRef = useRef(null);

    // Update local state when props change
    useEffect(() => {
        setLocalOpacity(opacity);
        setLocalBlur(blur);
        setLocalBackgroundBlur(backgroundBlur);
        setIsDarkMode(darkMode);
        setLocalBaseColor(baseColor);
        
        // Update CSS variables
        document.documentElement.style.setProperty('--opacity', opacity);
        document.documentElement.style.setProperty('--blur', blur);
        document.documentElement.style.setProperty('--background-blur', backgroundBlur);
        document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    }, [opacity, blur, backgroundBlur, darkMode, baseColor]);

    // Fetch unread message count
    useEffect(() => {
        const fetchUnreadCount = async () => {
            if (userId) {
                try {
                    const count = await messageService.getUnreadCount(userId);
                    setUnreadCount(count);
                } catch (error) {
                    console.error('Failed to fetch unread message count:', error);
                }
            }
        };

        fetchUnreadCount();

        // Refresh count every minute
        const interval = setInterval(fetchUnreadCount, 60000);
        return () => clearInterval(interval);
    }, [userId]);

    // Fetch notification count
    useEffect(() => {
        const fetchNotificationCount = async () => {
            if (userId) {
                try {
                    const summary = await NotificationService.getNotificationSummary(userId);
                    setNotificationCount(summary.unreadCount);
                } catch (error) {
                    console.error('Failed to fetch notification count:', error);
                }
            }
        };

        fetchNotificationCount();

        // Refresh count every minute
        const interval = setInterval(fetchNotificationCount, 60000);
        return () => clearInterval(interval);
    }, [userId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (appearanceMenuRef.current && !appearanceMenuRef.current.contains(event.target)) {
                setShowAppearanceMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (messageContainerRef.current && !messageContainerRef.current.contains(event.target)) {
                setShowMessages(false);
            }
            if (notificationContainerRef.current && !notificationContainerRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        // Handle search functionality
        console.log('Searching for:', searchQuery);
    };

    // Helper function to update preferences while preserving all values
    const updatePreferenceWithPreservation = async (updateKey, updateValue) => {
        try {
            // Create FormData using local state values instead of fetching from server
            const formData = new FormData();
            formData.append('formOpacity', (updateKey === 'formOpacity' ? updateValue : localOpacity).toString());
            formData.append('formBlur', (updateKey === 'formBlur' ? updateValue : localBlur).toString());
            formData.append('backgroundBlur', (updateKey === 'backgroundBlur' ? updateValue : localBackgroundBlur).toString());
            formData.append('darkMode', (updateKey === 'darkMode' ? updateValue : isDarkMode).toString());
            
            // Handle base color separately since it's a string
            if (updateKey === 'baseColor') {
                if (updateValue !== null && updateValue !== '') {
                    formData.append('baseColor', updateValue);
                }
            } else if (localBaseColor) {
                formData.append('baseColor', localBaseColor);
            }

            const response = await userPreferenceService.saveUserPreference(userId, formData);
            return response;
        } catch (error) {
            console.error(`Failed to save ${updateKey} preference:`, error);
            throw error;
        }
    };

    const handleOpacityChange = async (newOpacity) => {
        setLocalOpacity(newOpacity);
        try {
            await updatePreferenceWithPreservation('formOpacity', newOpacity);
            onOpacityChange(newOpacity);
        } catch (error) {
            console.error('Failed to save opacity preference:', error);
        }
    };

    const handleBlurChange = async (newBlur) => {
        setLocalBlur(newBlur);
        document.documentElement.style.setProperty('--blur', newBlur);
        try {
            await updatePreferenceWithPreservation('formBlur', newBlur);
            onBlurChange(newBlur);
        } catch (error) {
            console.error('Failed to save blur preference:', error);
        }
    };

    const handleBackgroundBlurChange = async (newBlur) => {
        setLocalBackgroundBlur(newBlur);
        document.documentElement.style.setProperty('--background-blur', newBlur);
        try {
            await updatePreferenceWithPreservation('backgroundBlur', newBlur);
            onBackgroundBlurChange(newBlur);
        } catch (error) {
            console.error('Failed to save background blur preference:', error);
        }
    };

    const handleThemeToggle = async () => {
        const newDarkMode = !isDarkMode;
        setIsDarkMode(newDarkMode);
        onThemeChange(newDarkMode);

        try {
            await updatePreferenceWithPreservation('darkMode', newDarkMode);
        } catch (error) {
            console.error('Failed to save theme preference:', error);
        }
    };

    const handleBaseColorChange = async (newColor) => {
        setLocalBaseColor(newColor);
        
        // Directly update CSS variables to reflect color change immediately
        if (newColor) {
            document.documentElement.style.setProperty('--accent-color', newColor);
            
            // Also update background color based on theme
            const rgb = hexToRgb(newColor);
            if (rgb) {
                document.documentElement.style.setProperty('--base-color-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
            }
        } else {
            document.documentElement.style.removeProperty('--accent-color');
            document.documentElement.style.removeProperty('--base-color-rgb');
        }
        
        try {
            await updatePreferenceWithPreservation('baseColor', newColor || '');
            onBaseColorChange(newColor);
        } catch (error) {
            console.error('Failed to save base color preference:', error);
        }
    };

    const handleResetPreferences = async () => {
        try {
            // Get default settings from the server
            const settingsResponse = await SettingsService.getSetting('userDashboard');
            const settings = settingsResponse?.value ? JSON.parse(settingsResponse.value) : null;

            if (!settings) {
                console.error('No default settings found');
                return;
            }

            // Save defaults to database
            const formData = new FormData();
            formData.append('formOpacity', (settings.FormOpacity || 1.0).toString());
            formData.append('formBlur', (settings.FormBlur || 1.0).toString());
            formData.append('backgroundBlur', (settings.BackgroundImageBlur || 0.0).toString());
            formData.append('darkMode', (settings.DarkMode || false).toString());
            formData.append('baseColor', settings.BaseColor || '');

            await userPreferenceService.saveUserPreference(userId, formData);

            // Just refresh the page - simple and it works
            window.location.reload();

        } catch (error) {
            console.error('Failed to reset preferences:', error);
        }
    };

    const toggleSectionVisibility = (panelId) => {
        onSectionVisibilityChange(panelId);
    };

    const getPanelIcon = (panel) => {
        if (panel.iconImage) {
            return (
                <img 
                    src={panel.iconImage.startsWith('http') ? panel.iconImage : `${API_BASE_URL}${panel.iconImage}`}
                    alt={panel.name} 
                    className="panel-image-icon" 
                />
            );
        }
        
        // Try to get the icon from react-icons/fa
        if (panel.iconCode) {
            const iconName = `Fa${panel.iconCode}`;
            const IconComponent = FaIcons[iconName];
            if (IconComponent) {
                return <IconComponent className="panel-icon" />;
            }
        }
        
        // Fallback to default panel icon
        return <FaTh className="default-panel-icon" />;
    };

    // Convert hex to RGB for background tinting
    const hexToRgb = (hex) => {
        if (!hex) return null;
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
        
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };
    
    const getBackgroundColor = () => {
        if (!baseColor) {
            return null; // Let CSS handle the default
        }
        
        // Convert base color to RGB
        const rgb = hexToRgb(baseColor);
        if (!rgb) return null;
        
        if (darkMode) {
            // Dark mode: Use the actual base color with opacity
            return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
        } else {
            // Light mode: Use a lighter tint of the base color
            // Mix with white to create a pleasant light version
            const lightFactor = 0.35; // Keep 35% of the original color
            const lightR = Math.round(rgb.r * lightFactor + 255 * (1 - lightFactor));
            const lightG = Math.round(rgb.g * lightFactor + 255 * (1 - lightFactor));
            const lightB = Math.round(rgb.b * lightFactor + 255 * (1 - lightFactor));
            return `rgba(${lightR}, ${lightG}, ${lightB}, ${opacity})`;
        }
    };

    // Add effect to update CSS variables when props change
    useEffect(() => {
        const bgColor = getBackgroundColor();
        if (bgColor) {
            document.documentElement.style.setProperty('--nav-bg-color', bgColor);
        } else {
            document.documentElement.style.removeProperty('--nav-bg-color');
        }
        
        // Set accent color for UI elements
        if (baseColor) {
            document.documentElement.style.setProperty('--accent-color', baseColor);
        } else {
            document.documentElement.style.removeProperty('--accent-color');
        }
    }, [baseColor, darkMode, opacity]);

    // Add this effect after the other useEffect hooks
    useEffect(() => {
        // Update CSS variables whenever opacity or blur changes
        document.documentElement.style.setProperty('--opacity', localOpacity);
        document.documentElement.style.setProperty('--blur', localBlur);
        document.documentElement.style.setProperty('--background-blur', localBackgroundBlur);
    }, [localOpacity, localBlur, localBackgroundBlur]);

    useEffect(() => {
        // Update theme whenever dark mode changes
        document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    const handleMessageClick = (message) => {
        setShowMessages(false); // Close the dropdown
        navigate(`/user-dashboard/messages?id=${message.id}`); // Navigate to the message
    };

    return (
        <div className="navbar-wrapper">
        <nav className="user-navbar">
            <div className="navbar-left">
                <form className="search-form" onSubmit={handleSearch}>
                    <div className="user-search-container">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </form>
            </div>

            <div className="navbar-right">
                <div className="nav-icons">
                    {panels.map(panel => (
                        <button 
                            key={panel.id}
                            className="icon-button"
                            onClick={() => toggleSectionVisibility(panel.id)}
                            title={`Toggle ${panel.name} Section`}
                        >
                            {getPanelIcon(panel)}
                            <span className="visibility-icon">
                                {sectionVisibility[panel.id] ? <FaEye /> : <FaEyeSlash />}
                            </span>
                        </button>
                    ))}

                        <div className="notification-button-container" ref={notificationContainerRef}>
                            <button 
                                className={`icon-button ${showNotifications ? 'active' : ''}`}
                                onClick={() => setShowNotifications(!showNotifications)}
                            >
                                <FaBell />
                                {notificationCount > 0 && (
                                    <span className="notification-badge">{notificationCount}</span>
                                )}
                            </button>

                            {showNotifications && (
                                <div className="notification-dropdown-container">
                                    <NotificationList 
                                        userId={userId}
                                        onNotificationClick={() => setShowNotifications(false)}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="message-button-container" ref={messageContainerRef}>
                            <button 
                                className={`icon-button ${showMessages ? 'active' : ''}`}
                                onClick={() => setShowMessages(!showMessages)}
                            >
                                <FaEnvelope />
                                {unreadCount > 0 && (
                                    <span className="notification-badge">{unreadCount}</span>
                                )}
                            </button>

                            {showMessages && (
                                <div ref={messageContainerRef} className="message-dropdown">
                                    <div className="messages-header">
                                        <div className="message-tabs">
                                            <button 
                                                className={`message-tab ${messageType === 'inbox' ? 'active' : ''}`}
                                                onClick={() => setMessageType('inbox')}
                                            >
                                                Inbox {unreadCount > 0 && `(${unreadCount})`}
                                            </button>
                                            <button 
                                                className={`message-tab ${messageType === 'sent' ? 'active' : ''}`}
                                                onClick={() => setMessageType('sent')}
                                            >
                                                Sent
                                            </button>
                                        </div>
                                    </div>
                                    <div className="messages-content">
                                        <MessageList 
                                            type={messageType}
                                            userId={userId}
                                            onMessageSelect={handleMessageClick}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                    <button 
                        className={`icon-button ${showAppearanceMenu ? 'active' : ''}`}
                        onClick={() => setShowAppearanceMenu(!showAppearanceMenu)}
                    >
                        <FaPalette />
                    </button>

                    {showAppearanceMenu && (
                        <div className="appearance-menu" ref={appearanceMenuRef}>
                            <div className="appearance-option">
                                <label>Theme</label>
                                <button 
                                    className={`theme-toggle ${isDarkMode ? 'dark' : ''}`}
                                    onClick={handleThemeToggle}
                                >
                                    <div className="toggle-thumb"></div>
                                    <span className="toggle-text">
                                        {isDarkMode ? 'Dark' : 'Light'}
                                    </span>
                                </button>
                            </div>
                            <div className="appearance-option">
                                <div className="label-with-picker">
                                    <label>Base Color</label>
                                    <div className="picker-controls">
                                        <input
                                            type="color"
                                            value={localBaseColor}
                                            onChange={(e) => handleBaseColorChange(e.target.value)}
                                            className="color-picker"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="appearance-option">
                                <label>Opacity ({Math.round(localOpacity * 100)}%)</label>
                                <input
                                    type="range"
                                    min="0.1"
                                    max="1"
                                    step="0.1"
                                    value={localOpacity}
                                    onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
                                    className="opacity-slider"
                                />
                            </div>
                            <div className="appearance-option">
                                <label>Blur Effect ({Math.round(localBlur * 100)}%)</label>
                                <input
                                    type="range"
                                    min="0.1"
                                    max="1"
                                    step="0.1"
                                    value={localBlur}
                                    onChange={(e) => handleBlurChange(parseFloat(e.target.value))}
                                    className="blur-slider"
                                />
                            </div>
                            <div className="appearance-option">
                                <label>Background Blur ({Math.round(localBackgroundBlur * 100)}%)</label>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={localBackgroundBlur}
                                    onChange={(e) => handleBackgroundBlurChange(parseFloat(e.target.value))}
                                    className="blur-slider"
                                />
                            </div>
                            <div className="appearance-option">
                                <button 
                                    className="reset-preferences-btn"
                                    onClick={handleResetPreferences}
                                    title="Reset all preferences to default values"
                                >
                                    Reset All Preferences
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </nav>
        </div>
    );
};

export default UserNavbar; 